import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import { PRO_MIN_PASSWORD_LENGTH } from '@erp/domain/pro';
import { useProAuth } from '@/lib/auth-context';
import { AuthLayout } from '@/components/layout/AuthLayout';

// Lands here after the user clicks the recovery link in the password
// reset email. Supabase JS detects the recovery token in the URL hash
// on init, exchanges it for a session, and emits PASSWORD_RECOVERY —
// captured by ProAuthProvider into `isPasswordRecovery`. This page
// shows a new-password form and calls `updatePassword` on submit.
export function UpdatePasswordPage() {
  const { t } = useTranslation();
  const { updatePassword, user, isPasswordRecovery, isLoading } = useProAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Aucune règle de composition — juste le plancher imposé par Supabase.
    if (password.length < PRO_MIN_PASSWORD_LENGTH) {
      setError(
        t('pro.auth.password_too_short', 'Le mot de passe doit faire au moins {{min}} caractères.', {
          min: PRO_MIN_PASSWORD_LENGTH,
        }),
      );
      return;
    }
    if (password !== confirm) {
      setError(t('pro.auth.password_mismatch', 'Les deux mots de passe ne correspondent pas.'));
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      navigate('/tableau-de-bord', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Block the page if there's no recovery session AND no logged-in
  // user — landing here without auth means a stale or invalid link.
  if (!isLoading && !user && !isPasswordRecovery) {
    return (
      <AuthLayout>
        <h2 className="text-xl font-semibold text-neutral-900 mb-1">
          {t('pro.auth.link_expired', 'Lien expiré ou invalide')}
        </h2>
        <p className="text-sm text-neutral-500 mb-6">
          {t(
            'pro.auth.request_new_link',
            "Le lien de réinitialisation n'est plus valide. Demandez un nouveau lien.",
          )}
        </p>
        <button
          onClick={() => navigate('/forgot-password')}
          className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{ background: 'var(--pro-primary)' }}
        >
          {t('pro.auth.forgot_title', 'Mot de passe oublié')}
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="text-xl font-semibold text-neutral-900 mb-1">
        {t('pro.auth.update_password_title', 'Nouveau mot de passe')}
      </h2>
      <p className="text-sm text-neutral-500 mb-6">
        {t('pro.auth.update_password_subtitle', 'Choisissez votre nouveau mot de passe.')}
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-neutral-700 mb-1.5 block">
            {t('pro.auth.new_password', 'Nouveau mot de passe')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="password"
              required
              minLength={PRO_MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-neutral-700 mb-1.5 block">
            {t('pro.auth.confirm_password', 'Confirmer')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="password"
              required
              minLength={PRO_MIN_PASSWORD_LENGTH}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
          style={{ background: 'var(--pro-primary)' }}
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('pro.auth.update_password_submit', 'Enregistrer')}
        </button>
      </form>
    </AuthLayout>
  );
}
